/**
 * KDF: key derivation from the E2E identity passphrase.
 *
 * Algorithm: **Argon2id** (`@noble/hashes`, audited pure JS — browser, Bun
 * and Node without native binaries). OWASP interactive-profile parameters. The `salt`
 * is a random 16 bytes per identity and is stored alongside the blob (public:
 * it is not a secret). Boveda/P-3 canon (ZERO_KNOWLEDGE_VAULT §3.1).
 *
 * ZERO-KNOWLEDGE INVARIANT: neither the passphrase nor the derived key is
 * ever serialized to the server — only opaque AES-GCM envelopes leave.
 /** Waits for the app to finish booting (skeleton overlay disappears). */

import { argon2id } from "@noble/hashes/argon2.js";

const DERIVED_KEY_BYTES = 32;
const KDF_SALT_BYTES = 16;

export interface Argon2Params {
  /** Memory cost in KiB. */
  readonly m: number;
  /** Iterations (time cost). */
  readonly t: number;
  /** Parallelism (lanes). */
  readonly p: number;
}

/** OWASP interactive preset (frequent unlocking / modest devices). */
export const KDF_INTERACTIVE: Argon2Params = { m: 19_456, t: 2, p: 1 };

export function newKdfSalt(): Uint8Array {
  const s = new Uint8Array(KDF_SALT_BYTES);
  crypto.getRandomValues(s);
  return s;
}

/**
 * Derives the key (32 bytes) from passphrase + salt with Argon2id. Pure and
 * deterministic for the same (passphrase, salt, params). NFKC-normalized.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export function deriveKdfKey(
  passphrase: string,
  salt: Uint8Array,
  params: Argon2Params = KDF_INTERACTIVE,
): Uint8Array {
  if (salt.length < KDF_SALT_BYTES) {
    throw new Error("salt too short");
  }
  return argon2id(new TextEncoder().encode(passphrase.normalize("NFKC")), salt, {
    m: params.m,
    t: params.t,
    p: params.p,
    dkLen: DERIVED_KEY_BYTES,
  });
}
